import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'react-toastify';
import {
  deleteRouteConfiguration,
  getRouteConfiguration,
  updateRouteConfiguration,
} from 'routes/api/routesApi';
import { SessionExpiredError } from 'shared/api/apiUtils';
import { queryKeys } from 'shared/api/queryKeys';
import { Connection } from 'shared/types/ObjectTypes';
import { FlowDirectionKey } from '../flowMap/flowMapTypes';
import { resolveRulesTarget } from '../flowMap/rulesTarget';
import {
  FieldErrors,
  FieldMapping,
  FieldMappingsConfig,
  STREAM_TYPES,
  StreamType,
  buildConfigWithRule,
  buildConfigWithoutRule,
  getRuleForDestination,
  parseServerErrors,
} from '../transformationRules/fieldMappingTypes';
import type { RouteRecord } from './useRouteRecord';
import type { ReverseConfigBySite } from './useRelatedRoutes';

// Stable empty for a configuration that hasn't loaded yet, so memos that
// depend on it don't re-run on every render.
const EMPTY_FIELD_MAPPINGS: FieldMappingsConfig = { field_mappings: {} };

export type RulesEditor = 'device' | 'field_mapping';

// The Rules side panel and the field mappings it edits: this Route's own
// configuration, and the reverse Route's for the way back.
export const useRouteRules = ({
  record,
  reverseConfigBySite,
  storeReverseConfig,
  onPanelOpenChange,
}: {
  record: RouteRecord;
  reverseConfigBySite: ReverseConfigBySite;
  storeReverseConfig: (
    siteId: string,
    data: FieldMappingsConfig,
    name?: string,
  ) => void;
  // Told when the Rules panel opens or closes, for the page's Help panel.
  onPanelOpenChange: (open: boolean) => void;
}) => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const { getToken, route, routeProvider, defaultRouteId, pageLoaded } = record;

  const [rulesPanelOpen, setRulesPanelOpen] = useState(false);
  // Which rule's editor the panel opens on; null only before the first open.
  const [rulesInitialEditor, setRulesInitialEditor] =
    useState<RulesEditor | null>(null);
  // Bumped on every open request, so Edit on another rule's card reaches the editor
  // even when the panel is already open (isOpen alone has no edge to fire on then).
  const [rulesEditorNonce, setRulesEditorNonce] = useState(0);
  const [rulesPanelDestination, setRulesPanelDestination] =
    useState<Connection | null>(null);
  // Which arrow the rules panel was opened from. "out" = this Route (provider →
  // site); "in" = the reverse Route (site → provider), whose field mappings live
  // on that other route record.
  const [rulesDirection, setRulesDirection] = useState<FlowDirectionKey>('out');

  // This Route's own field mappings. Keyed by the route RECORD id the PATCHes
  // target — never the page's :route_id URL param, which is the provider
  // integration id. The configuration's NAME is cached with them: a PATCH
  // replaces the whole configuration payload, so every save has to resend it.
  const routeConfigQuery = useQuery({
    queryKey: queryKeys.routes.configuration(defaultRouteId ?? ''),
    queryFn: async () => {
      const token = getToken();
      if (!token) throw new SessionExpiredError();
      const configuration = await getRouteConfiguration(
        token,
        defaultRouteId as string,
      );
      // Undefined means the session expired (handleError swallowed the
      // 401). Never store that as an empty configuration: a save built
      // from it would PATCH every destination's real mappings away.
      if (configuration === undefined) throw new SessionExpiredError();
      return {
        data: (configuration?.configuration?.data ?? {
          field_mappings: {},
        }) as FieldMappingsConfig,
        name: (configuration?.configuration?.name ?? '') as string,
      };
    },
    enabled: pageLoaded && !!defaultRouteId && !!getToken(),
  });
  const routeConfigData: FieldMappingsConfig =
    routeConfigQuery.data?.data ?? EMPTY_FIELD_MAPPINGS;
  const routeConfigurationName = routeConfigQuery.data?.name ?? '';

  const handleOpenRules = (
    destination: Connection,
    direction: FlowDirectionKey,
    editor: RulesEditor,
  ) => {
    setRulesPanelDestination(destination);
    setRulesDirection(direction);
    setRulesInitialEditor(editor);
    setRulesEditorNonce((n) => n + 1);
    setRulesPanelOpen(true);
    onPanelOpenChange(true);
  };

  const handleCloseRules = () => {
    setRulesPanelOpen(false);
    onPanelOpenChange(false);
  };

  // Everything the rules panel needs about the flow it was opened for. The two
  // directions are two different route records with the provider/destination
  // roles swapped, so resolving them in one place keeps save, delete and the
  // panel's own display from disagreeing about which flow is being edited.
  const rulesTarget = useMemo(() => {
    const resolved = resolveRulesTarget({
      direction: rulesDirection,
      site: rulesPanelDestination,
      provider: routeProvider ?? route.provider,
      routeRecordId: defaultRouteId,
      routeConfigName: routeConfigurationName,
      routeConfigData: routeConfigData,
      reverseConfigBySite,
    });
    if (!resolved) return null;
    // The panel labels the other end of the flow, so it needs the record, not
    // just its id: the site going out, our provider coming back.
    const counterpart = (
      rulesDirection === 'out'
        ? rulesPanelDestination
        : (routeProvider ?? route.provider)
    ) as Connection;
    return { ...resolved, counterpart };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    rulesPanelDestination,
    rulesDirection,
    defaultRouteId,
    routeProvider,
    routeConfigurationName,
    routeConfigData,
    reverseConfigBySite,
    route.provider,
  ]);

  // Where a saved/deleted rule lands: this Route's own configuration state, or
  // the cached configuration of the reverse Route.
  const storeRulesConfig = (data: FieldMappingsConfig, name?: string) => {
    if (rulesDirection === 'out') {
      queryClient.setQueryData(
        queryKeys.routes.configuration(defaultRouteId ?? ''),
        (prev: { data: FieldMappingsConfig; name: string } | undefined) => ({
          data,
          name: name ?? prev?.name ?? '',
        }),
      );
      return;
    }
    if (!rulesPanelDestination) return;
    storeReverseConfig(rulesPanelDestination.id, data, name);
  };

  const handleSaveRules = async (
    streamType: StreamType,
    mapping: FieldMapping,
    name: string,
  ): Promise<FieldErrors | void> => {
    // The record the PATCH targets is the flow's OWN route: this Route for the
    // outgoing direction, the reverse Route for the incoming one. Neither is
    // the page's :route_id URL param, which is the provider integration id.
    const recordId = rulesTarget?.recordId;
    const providerId = rulesTarget?.providerId;
    const destinationId = rulesTarget?.counterpart?.id;
    if (!recordId || !providerId || !destinationId) {
      // Field mappings live on the route's configuration, so with no route
      // there is nothing to save. Returning quietly reads as a dead button.
      console.log('cannot save rule', { recordId, providerId, destinationId });
      toast.error(`${t('Failed to save rule')}`, {
        position: toast.POSITION.TOP_RIGHT,
      });
      // Rejecting, not returning: a resolved save reads as "it landed" to the
      // panel and the leave-page prompt, which would then move on.
      throw new Error('Rule save preconditions not met');
    }
    const token = getToken();
    if (!token) throw new Error('No session token');
    // buildConfigWithRule preserves any other keys (subject_type, ca_uuids, …) that
    // the route's configuration.data already carries, so the PATCH-replaces-data
    // semantics don't blow them away.
    const nextConfig = buildConfigWithRule(
      rulesTarget.data,
      providerId,
      destinationId,
      streamType,
      mapping,
    );
    try {
      await updateRouteConfiguration(token, recordId, {
        name,
        data: nextConfig,
      });
      storeRulesConfig(nextConfig, name);
      toast.success(`${t('Rule saved')}`, {
        position: toast.POSITION.TOP_RIGHT,
      });
      handleCloseRules();
    } catch (err: any) {
      // 400s carry field-level validation errors keyed by dotted path. We surface
      // them on the panel so the user knows what to fix; anything else falls back
      // to the generic save-failed toast.
      const responseStatus = err?.response?.status;
      if (responseStatus === 400 && err.response?.data) {
        const fieldErrors = parseServerErrors(
          err.response.data,
          providerId,
          destinationId,
          streamType,
        );
        if (Object.keys(fieldErrors).length > 0) return fieldErrors;
      }
      console.log(err);
      toast.error(`${t('Failed to save rule')}`, {
        position: toast.POSITION.TOP_RIGHT,
      });
      throw err;
    }
  };

  const handleDeleteRule = async () => {
    // Same resolution as the save above: the flow's own route record, with the
    // provider/destination roles as that direction sees them.
    const recordId = rulesTarget?.recordId;
    const providerId = rulesTarget?.providerId;
    const destinationId = rulesTarget?.counterpart?.id;
    if (!recordId || !providerId || !destinationId) {
      // Field mappings live on the route's configuration, so with no route
      // there is nothing to delete. Returning quietly reads as a dead button.
      console.log('cannot delete rule', {
        recordId,
        providerId,
        destinationId,
      });
      toast.error(`${t('Failed to delete rule')}`, {
        position: toast.POSITION.TOP_RIGHT,
      });
      return;
    }
    const token = getToken();
    if (!token) return;
    try {
      const nextConfig = buildConfigWithoutRule(
        rulesTarget.data,
        providerId,
        destinationId,
      );
      const fieldMappingsEmpty =
        !nextConfig.field_mappings ||
        Object.keys(nextConfig.field_mappings).length === 0;
      const otherKeysEmpty =
        Object.keys(nextConfig).filter((k) => k !== 'field_mappings').length ===
        0;
      const isEffectivelyEmpty = fieldMappingsEmpty && otherKeysEmpty;

      if (isEffectivelyEmpty) {
        // Try the dedicated DELETE endpoint first so the backend doesn't keep an
        // empty row around. If it isn't deployed yet on the env we're hitting,
        // some infra returns the SPA's index.html for unknown API paths and our
        // apiUtils interceptor reads that as a Keycloak redirect — falsely
        // triggering the session-expired modal. To keep delete working until the
        // backend PR is merged we fall back to a PATCH with empty data on any
        // error other than a real auth failure.
        try {
          await deleteRouteConfiguration(token, recordId);
          storeRulesConfig({ field_mappings: {} }, '');
        } catch (deleteErr: any) {
          if (deleteErr?.response?.status === 401) throw deleteErr;
          console.log(
            'DELETE configuration not available, falling back to PATCH:',
            deleteErr,
          );
          await updateRouteConfiguration(token, recordId, {
            name: rulesTarget.configName,
            data: nextConfig,
          });
          storeRulesConfig(nextConfig);
        }
      } else {
        // Other rules / sibling keys remain — PATCH with the cleaned data.
        await updateRouteConfiguration(token, recordId, {
          name: rulesTarget.configName,
          data: nextConfig,
        });
        storeRulesConfig(nextConfig);
      }
      toast.success(`${t('Rule deleted')}`, {
        position: toast.POSITION.TOP_RIGHT,
      });
      handleCloseRules();
    } catch (err) {
      console.log(err);
      toast.error(`${t('Failed to delete rule')}`, {
        position: toast.POSITION.TOP_RIGHT,
      });
    }
  };

  const currentPanelRule = useMemo(() => {
    if (!rulesTarget?.providerId || !rulesTarget.counterpart?.id) return null;
    return getRuleForDestination(
      rulesTarget.data,
      rulesTarget.providerId,
      rulesTarget.counterpart.id,
    );
  }, [rulesTarget]);

  // IDs of destinations that currently have a transformation rule under this
  // provider. Drives the rule badge on the flow map's outgoing arrows.
  const destinationsWithRules = useMemo<Set<string>>(() => {
    const ids = new Set<string>();
    const providerId = routeProvider?.id;
    const providerEntry = providerId
      ? routeConfigData.field_mappings?.[providerId]
      : undefined;
    if (!providerEntry) return ids;
    for (const streamType of STREAM_TYPES) {
      const streamEntry = providerEntry[streamType];
      if (streamEntry) Object.keys(streamEntry).forEach((id) => ids.add(id));
    }
    return ids;
  }, [routeConfigData, routeProvider?.id]);

  // Same badge for the way back: the reverse Route's own configuration, where
  // the site is the provider and our provider is the destination.
  const reverseDestinationsWithRules = useMemo<Set<string>>(() => {
    const ids = new Set<string>();
    const providerId = routeProvider?.id;
    if (!providerId) return ids;
    Object.entries(reverseConfigBySite).forEach(([siteId, reverse]) => {
      const siteEntry = reverse.data?.field_mappings?.[siteId];
      if (!siteEntry) return;
      for (const streamType of STREAM_TYPES) {
        if (siteEntry[streamType]?.[providerId]) ids.add(siteId);
      }
    });
    return ids;
  }, [reverseConfigBySite, routeProvider?.id]);

  return {
    routeConfigData,
    routeConfigurationName,
    rulesPanelOpen,
    rulesInitialEditor,
    rulesEditorNonce,
    rulesPanelDestination,
    rulesDirection,
    rulesTarget,
    currentPanelRule,
    destinationsWithRules,
    reverseDestinationsWithRules,
    handleOpenRules,
    handleCloseRules,
    handleSaveRules,
    handleDeleteRule,
  };
};
